/**
 * Formulas menu: list of saved formulas to choose from
 */
import {jsx} from 'panel';

import {InputComponent} from 'common/components/input';
import {ItemClickEvent, Section} from 'common/components/items-menu/types';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {ItemsMenuEl} from 'common/components/items-menu';
import {SkeletonListEl} from 'common/components/skeleton-list';
import {SkeletonListRowSize} from 'common/components/skeleton-list/types';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {getResolvedFormulaEntry} from 'common/query-builder/query-builder/util';

import {ChildMenu} from '../child-menu';
import {MenuSearchBar} from '../mixins/search';
import css from './index.inline.styl';
import {FormulaMetricId} from './types';

interface FormulasMenuState {
  searchFilter: string;
}

export class FormulasMenu extends ChildMenu<FormulasMenuState> {
  public searchInputEl: InputComponent = null;

  get config() {
    return {
      contexts: [`sessionStorageManager` as const, `currentProject` as const, `metrics` as const],
      defaultContexts: {
        sessionStorageManager: new DummyStorageManager(),
        metrics: new MetricsResource(),
      },
      defaultState: {
        searchFilter: ``,
      },
      helpers: {
        ...super.config.helpers,
      },
      useShadowDom: true,
      css,
      template: () => {
        const isLoading = this.getContext(`metrics`).isLoadingAll();

        const InnerSearch = MenuSearchBar({
          value: this.state.searchFilter,
          handleSearchInput: (value) => this.update({searchFilter: value}),
          onInsert: (el) => this.update({keydownProxyEl: el}),
          placeholder: `Search Formulas`,
          newStyling: true,
        });

        return (
          <div
            sel='.mp-formulas-menu'
            class={{'mp-child-menu': true}}
            hook={{insert: (vnode) => this.helpers.setChildMenuRef(vnode)}}
          >
            {this.attr(`open`) && (
              <>
                {InnerSearch}
                {isLoading ? (
                  <SkeletonListEl
                    attrs={{'num-rows': 6, 'row-size': SkeletonListRowSize.Large}}
                    sel='.mp-formulas-menu-skeleton-list'
                  />
                ) : (
                  <ItemsMenuEl
                    sel='.mp-formulas-menu-items-menu'
                    attrs={{
                      'empty-message': `No items found`,
                      multiple: false,
                      'search-filter': this.state.searchFilter,
                      'start-focus-on-selected': true,
                      'disable-search-sort': true,
                      'disable-search-filter-matching-parts': true,
                      'disable-first-item-padding': true,
                      'new-styling': true,
                    }}
                    on={{
                      click: (ev) => ev.stopPropagation(),
                      clickItem: (ev) => this.handleClickItem(ev),
                    }}
                    props={{
                      sections: this.getSections(),
                      keydownProxyEl: !this.state.isModalOpen ? document.body : this.state.keydownProxyEl,
                    }}
                  />
                )}
                <ButtonEl
                  sel='.create-custom-button'
                  attrs={{
                    'show-preview-tooltip': false,
                    theme: ButtonTheme.MenuFooter,
                    'hide-input-focus-styling': true,
                    label: `Create Custom`,
                  }}
                  on={{
                    click: () => this.handleClickCreate(),
                  }}
                >
                  <div sel='.create-custom-label' attrs={{elref: `label`}}>
                    Create Custom
                  </div>
                </ButtonEl>
              </>
            )}
          </div>
        );
      },
    };
  }

  handleClickCreate() {
    this.dispatchEvent(new CustomEvent(`create`));
  }

  handleClickItem({detail}: ItemClickEvent<FormulaMetricId>) {
    const {item} = detail;

    this.dispatchEvent(new CustomEvent(`select`, {detail: {item: item.value}}));
  }

  getPersistenceKey() {
    return `formulas-menu`;
  }

  getSections(): Array<Section<FormulaMetricId>> {
    /** filters metrics according to the user's permissions and sharing */
    const savedFormulas = this.getContext(`metrics`)
      .getAllValues()
      .filter((metric) => metric.type === `formula` && metric.can_view);

    if (savedFormulas.length) {
      return [
        {
          items: savedFormulas.map((metric) => ({
            label: metric.name,
            value: metric.id,
          })),
        },
      ];
    } else {
      return [];
    }
  }

  selectedLabel() {
    const selected = this.attr(`selected`);

    if (selected?.id) {
      return (
        getResolvedFormulaEntry({
          metricResource: this.getContext(`metrics`),
          formulaEntry: {event: selected},
        })?.event.name || `Loading...`
      );
    }

    return `Select Formula`;
  }
}

export const FormulasMenuEl = defineAndMakeJsxFactory(`mp-formulas-menu`, FormulasMenu);
export const FormulasMenuReact = panel2react(`mp-formulas-menu`, FormulasMenu);
